import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowLeft, Loader2, Newspaper } from 'lucide-react';
import { MarketAnalysisReport } from '@/components/MarketAnalysisReport';
import { marketAnalysisService, type MarketAnalysisPayload } from '@/services/api';

export function MarketAnalysisPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<MarketAnalysisPayload | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      const response = await marketAnalysisService.getLatest({
        date: '2026-07-29',
        topK: 20,
      });
      if (cancelled) return;
      if (!response?.analysis) {
        setError('No market analysis is available yet.');
        setAnalysis(null);
      } else {
        setAnalysis(response.analysis);
      }
      setLoading(false);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="market-analysis-page flex min-h-screen flex-col bg-gray-50 print:bg-white">
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="market-analysis-page__header shrink-0 border-b border-gray-200 bg-white print:hidden"
      >
        <div className="mx-auto flex w-full max-w-7xl items-center gap-4 px-6 py-4">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="rounded-lg p-2 transition-colors hover:bg-gray-100"
            aria-label="Back to dashboard"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-900">
              <Newspaper className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="tracking-tight text-gray-900">Market Analysis</h2>
              <p className="truncate text-sm text-gray-500">
                {analysis?.reference_date_label ?? analysis?.proxy_label ?? 'S&P 500 / broad market'}
              </p>
            </div>
          </div>
        </div>
      </motion.header>

      <main className="market-analysis-page__main mx-auto w-full max-w-7xl flex-1 px-6 py-8 md:py-10 print:max-w-none print:p-0">
        <div className="market-analysis-page__shell overflow-hidden rounded-3xl border border-gray-200 bg-white print:overflow-visible print:rounded-none print:border-0 print:shadow-none">
          {loading ? (
            <div className="flex items-center justify-center gap-3 px-6 py-24">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              <span className="text-gray-500">Loading market analysis…</span>
            </div>
          ) : error ? (
            <div className="px-6 py-24 text-center">
              <p className="text-gray-600">{error}</p>
            </div>
          ) : analysis ? (
            <MarketAnalysisReport analysis={analysis} />
          ) : null}
        </div>
      </main>
    </div>
  );
}
